import {
    computed,
    onBeforeUnmount,
    ref,
    watch,
    type ComputedRef,
    type Ref,
} from 'vue';
import type { Subscription } from 'rxjs';
import {
    MeasureLineVisibilityGroup,
    MeasurePairMeasurementCalloutKind,
    type MeasureCalloutViewState,
    type MeasurePairMeasurementCalloutItem,
    type MeasurePairMeasurementViewState,
    type Viewer,
} from '@puppycad/cad-core';

/**
 * 测量结果面板组合函数参数。
 */
export interface UseMeasureResultPanelOptions {
    /** 获取 Viewer 实例。 */
    getViewer: () => Viewer | null;
    /** 测量会话是否激活。 */
    isMeasureActive: Ref<boolean>;
}

/**
 * 测量结果面板对外状态。
 */
export interface MeasureResultPanelController {
    /** 单选属性视图状态。 */
    singleViewState: ComputedRef<MeasureCalloutViewState | null>;
    /** 双选距离视图状态。 */
    pairMeasurement: ComputedRef<MeasurePairMeasurementViewState | null>;
    /** 当前测量线可视化选中组（点选钉住，驱动 UI 选中态）。 */
    lineVisibilityGroup: ComputedRef<MeasureLineVisibilityGroup>;
    /** 结果区是否有可展示内容。 */
    hasResultContent: ComputedRef<boolean>;
    /**
     * 处理结果项点选。
     *
     * @param item 结果项。
     */
    handleResultItemSelect: (item: MeasurePairMeasurementCalloutItem) => void;
    /**
     * 处理结果项指针进入（hover 预览测量线）。
     *
     * @param item 结果项。
     */
    handleResultItemHoverEnter: (item: MeasurePairMeasurementCalloutItem) => void;
    /**
     * 处理结果项指针离开（清除 hover 预览，恢复钉住组）。
     */
    handleResultItemHoverLeave: () => void;
}

/**
 * 将结果项种类映射为可视化选中组。
 *
 * @param kind 结果项种类。
 * @returns 选中组。
 */
export function resolveLineVisibilityGroupFromCalloutKind(
    kind: MeasurePairMeasurementCalloutKind,
): MeasureLineVisibilityGroup {
    switch (kind) {
        case MeasurePairMeasurementCalloutKind.Distance:
            return MeasureLineVisibilityGroup.Distance;
        case MeasurePairMeasurementCalloutKind.NormalDistance:
            return MeasureLineVisibilityGroup.NormalDistance;
        case MeasurePairMeasurementCalloutKind.Dx:
            return MeasureLineVisibilityGroup.Dx;
        case MeasurePairMeasurementCalloutKind.Dy:
            return MeasureLineVisibilityGroup.Dy;
        case MeasurePairMeasurementCalloutKind.Dz:
            return MeasureLineVisibilityGroup.Dz;
        default:
            return MeasureLineVisibilityGroup.None;
    }
}

/**
 * 判断结果项是否属于当前选中组（用于高亮）。
 *
 * @param kind 结果项种类。
 * @param group 当前选中组。
 * @returns 是否高亮。
 */
export function isMeasureResultItemActive(
    kind: MeasurePairMeasurementCalloutKind,
    group: MeasureLineVisibilityGroup,
): boolean {
    return resolveLineVisibilityGroupFromCalloutKind(kind) === group
        && group !== MeasureLineVisibilityGroup.None;
}

/**
 * 创建测量结果面板控制器（订阅 cad-core 并转发点选 / hover 可见性）。
 *
 * @param options 组合函数参数。
 * @returns 面板控制器。
 */
export function useMeasureResultPanel(
    options: UseMeasureResultPanelOptions,
): MeasureResultPanelController {
    const singleViewState = ref<MeasureCalloutViewState | null>(null);
    const pairMeasurement = ref<MeasurePairMeasurementViewState | null>(null);
    const lineVisibilityGroup = ref<MeasureLineVisibilityGroup>(MeasureLineVisibilityGroup.None);

    let calloutSubscription: Subscription | null = null;

    /**
     * 清空本地结果态。
     */
    function resetLocalState(): void {
        singleViewState.value = null;
        pairMeasurement.value = null;
        lineVisibilityGroup.value = MeasureLineVisibilityGroup.None;
    }

    /**
     * 订阅 cad-core callout 状态流。
     */
    function subscribeCalloutChanges(): void {
        calloutSubscription?.unsubscribe();
        calloutSubscription = null;

        const viewer = options.getViewer();
        if (!viewer) {
            resetLocalState();
            return;
        }

        const calloutManager = viewer.measureController.getCalloutManager();
        calloutSubscription = calloutManager.observeCalloutChange().subscribe((event) => {
            singleViewState.value = event.viewState;
            pairMeasurement.value = event.pairMeasurement;
            lineVisibilityGroup.value = event.lineVisibilityGroup;
        });

        singleViewState.value = calloutManager.getCalloutViewState();
        pairMeasurement.value = calloutManager.getPairMeasurementViewState();
        lineVisibilityGroup.value = calloutManager.getLineVisibilityGroup();
    }

    /**
     * 处理结果项点选：切换对应测量线钉住组。
     *
     * @param item 结果项。
     */
    function handleResultItemSelect(item: MeasurePairMeasurementCalloutItem): void {
        const viewer = options.getViewer();
        if (!viewer || !options.isMeasureActive.value) {
            return;
        }
        const group = resolveLineVisibilityGroupFromCalloutKind(item.kind);
        viewer.measureController.setLineVisibilityGroup(group, { toggleSame: true });
    }

    /**
     * 处理结果项 hover 进入：预览对应测量线。
     *
     * @param item 结果项。
     */
    function handleResultItemHoverEnter(item: MeasurePairMeasurementCalloutItem): void {
        const viewer = options.getViewer();
        if (!viewer || !options.isMeasureActive.value) {
            return;
        }
        const group = resolveLineVisibilityGroupFromCalloutKind(item.kind);
        viewer.measureController.setLineVisibilityHoverPreview(group);
    }

    /**
     * 处理结果项 hover 离开：清除预览，恢复钉住组。
     */
    function handleResultItemHoverLeave(): void {
        const viewer = options.getViewer();
        if (!viewer || !options.isMeasureActive.value) {
            return;
        }
        viewer.measureController.setLineVisibilityHoverPreview(null);
    }

    const hasResultContent = computed(() => {
        if (!options.isMeasureActive.value) {
            return false;
        }
        if (pairMeasurement.value && pairMeasurement.value.callouts.length > 0) {
            return true;
        }
        return singleViewState.value !== null;
    });

    watch(
        () => options.isMeasureActive.value,
        (active) => {
            if (!active) {
                calloutSubscription?.unsubscribe();
                calloutSubscription = null;
                resetLocalState();
                return;
            }
            subscribeCalloutChanges();
        },
        { immediate: true },
    );

    onBeforeUnmount(() => {
        calloutSubscription?.unsubscribe();
        calloutSubscription = null;
    });

    return {
        singleViewState: computed(() => singleViewState.value),
        pairMeasurement: computed(() => pairMeasurement.value),
        lineVisibilityGroup: computed(() => lineVisibilityGroup.value),
        hasResultContent,
        handleResultItemSelect,
        handleResultItemHoverEnter,
        handleResultItemHoverLeave,
    };
}
